import dayjs from 'dayjs'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'
import React, { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { CompanyForm } from './CompanyForm'
import { API_COMPANIES } from '../../../../../constants'
import { Company } from '../../../../../model/company.model'
import { RequestMethod } from '../../../../../model/enums/enums'
import { addCompany } from '../../../../../redux/basic/company/companyActions'
import { useAppDispatch } from '../../../../../redux/hooks'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { BackButton } from '../../../../util/button/BackButton'

export const AddCompanyEntry = () => {
  const emptyCompany: Company = {
    company_id: undefined,
    id_app_admin: undefined,
    id_ceo: undefined,
    id_company_relationship_type: undefined,
    onrp: undefined,
    uid: undefined,
    uid_addition: undefined,
    company_name: undefined,
    address: undefined,
    p_o_box: undefined,
    phone: undefined,
    company_form: undefined,
    email: undefined,
    bank_name: undefined,
    iban: undefined,
    company_description: undefined,
    founding_date: undefined,
    legal_activity_ct: undefined,
    company_form_ct: undefined,
    slack_workspace_url: undefined,
    number_of_employees_ct: 0,
    number_of_apprentices_ct: 0,
    is_active: undefined,
    has_agreed_to_terms_and_conditions: undefined,
    company_logo_reference: undefined,
    christmas_bonus: undefined,
    bonus: 0,
    vacation_days: 0,
  }
  const dispatcher = useAppDispatch()
  const msg = useRef<Messages>(null)

  const formMethods = useForm({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: { ...emptyCompany },
  })

  const reset = () => {
    formMethods.reset({ ...emptyCompany })
    msg?.current?.show({
      severity: 'info',
      sticky: false,
      life: 8000,
      closable: true,
      detail: t<string>('company.message.reset'),
    })
  }

  useEffect(() => {
    // console.log(formMethods.formState.errors)
    if (Object.keys(formMethods.formState.errors).length !== 0) {
      console.warn(
        '[Add Company] found at least one error in the form. Displaying user error.'
      )
      msg?.current?.show({
        severity: 'error',
        sticky: true,
        life: 8000,
        closable: true,
        detail: t<string>('company.message.add.error'),
      })
    }
  }, [formMethods.formState.errors])

  const navigate = useNavigate()

  const handleSubmit = async (company: Company) => {
    console.log('[Add Company] submit pressed.')
    msg.current?.clear()

    // format timestamp to mysql valid date
    if (company.founding_date) {
      const validDate = dayjs(company.founding_date).format('YYYY-MM-DD')
      company.founding_date = validDate ? validDate : undefined
    }

    if (company.founding_date === null) {
      company.founding_date = undefined
    }

    // empty string will cause error on backend - empty string is set, when iban is invalid
    if (company.iban === '') {
      company.iban = undefined
    }

    const response = await handleRequest<Company>(
      API_COMPANIES(),
      RequestMethod.POST,
      company
    )

    if (response?.data && response.error === undefined) {
      dispatcher(addCompany(response.data))
      msg?.current?.show({
        severity: 'success',
        sticky: true,
        life: 8000,
        closable: true,
        detail: t<string>('company.message.add.success'),
      })
    } else {
      msg?.current?.show({
        severity: 'error',
        sticky: true,
        life: 8000,
        closable: true,
        detail: t<string>('company.message.add.failed'),
      })
    }
  }

  // do not create the company and return to the previous page (company overview)
  const cancel = () => {
    console.log('[Add Company] Cancel pressed.')
    navigate(AuthRoutes.company)
  }

  return (
    <div className={'card pr-2'}>
      <BackButton translationKey={'company.back'} route={AuthRoutes.company} />
      <FormProvider {...formMethods}>
        <form
          className={'ml-2'}
          onSubmit={formMethods.handleSubmit(handleSubmit)}
          autoComplete={'off'}
        >
          {CompanyForm(formMethods)}
          <div className={'flex w-full gap-2'}>
            <Button
              label={t<string>('core.clear_form')}
              className={'ml-auto p-button-text'}
              onClick={() => reset()}
              type={'button'}
            />
            <Button
              label={t<string>('core.cancel')}
              className={'p-button-secondary mr-2 '}
              onClick={() => cancel()}
              type={'button'}
            />
            <Button label={t<string>('core.save')} type={'submit'} />
          </div>
        </form>
      </FormProvider>
      <Messages ref={msg} className={'mt-6'} />
    </div>
  )
}